import { messstelle, spannungswandler, stromwandler, schaltfeld, umspannwerk, spannungsebene } from './../../../utils/apiTypes';
import {
  messstelleState
} from "./messstellenTypes";
import { AnyAction, createSlice, PayloadAction, ThunkAction } from "@reduxjs/toolkit";
import { deleteWithAuth, getWithAuth, putWithAuth } from "../../../utils/api";
import type { RootState, AppDispatch } from "../../../app/store";
import { logError } from "../../../utils/utils";
import { openSnackbar } from "../../../features/snackbar/globalSnackbarSlice";
import { GridSortModel } from '@material-ui/data-grid';
import { clearMessstellenForLookup } from '../../../features/messstellenLookup/messstellenLookupSlice';
import { errorHandler } from '../../../utils/errorHandling';

const elementNotFoud = -1;

export const messstelleDummy: messstelle = {
  name: "",
  breitengrad: 0,
  laengengrad: 0,
  schaltfeld: "",
  spannungsebene: "",
  stromwandler: "",
  spannungswandler: "",
  umspannwerk: "",
  kommentar: "",
  hatDaten: false,
  zugeordnet: false,
  jemalsZugeordnet: false,
};

const initialState: messstelleState = {
  editEntry: false,
  newEntry: false,
  messstellenList: [],
  alteMessstelle: messstelleDummy,
  aktuelleMessstelle: messstelleDummy,
  uwList: [],
  schaltfelder: [],
  spannungsebenen: [],
  stromwandler: [],
  spannungswandler: [],
  dublicateName: false,
  editDirty: false,
  viewMode: false,
  error: [],
  uwKoordinatenUebernehmenDialog: false,
  listLoading: false,
  uwLoading: false,
  spannungswandlerLoading: false,
  stromwandlerLoading: false,
  spannungsebeneLoading: false,
  schaltfeldLoading: false,
  sorting: [
    {
      field: 'name',
      sort: 'asc',
    },
  ]
};
const messstellenNotEqual = (
  messstelleAktuell: messstelle,
  messstelleAlt: messstelle
): boolean => {
  if (
    messstelleAktuell.name == messstelleAlt.name &&
    messstelleAktuell.breitengrad == messstelleAlt.breitengrad &&
    messstelleAktuell.laengengrad == messstelleAlt.laengengrad &&
    (messstelleAktuell.kommentar == messstelleAlt.kommentar ||
      (messstelleAktuell.kommentar == "" && messstelleAlt.kommentar == null)) &&
    messstelleAktuell.umspannwerk == messstelleAlt.umspannwerk &&
    messstelleAktuell.schaltfeld == messstelleAlt.schaltfeld &&
    messstelleAktuell.spannungsebene == messstelleAlt.spannungsebene &&
    messstelleAktuell.stromwandler == messstelleAlt.stromwandler &&
    messstelleAktuell.spannungswandler == messstelleAlt.spannungswandler
  )
    return false;
  return true;
};

const messstellenSlice = createSlice({
  name: "messstellen",
  initialState,
  reducers: {
    updateMessstellen: (state, action: PayloadAction<messstelle>) => {
      const updateIndex = state.messstellenList.findIndex(
        (x) => x.name == action.payload.name
      );
      const firstPart = state.messstellenList.slice(0, updateIndex);
      let lastPart = Array<messstelle>();
      if (updateIndex + 1 < state.messstellenList.length)
        lastPart = state.messstellenList.slice(updateIndex + 1);
      state.messstellenList = [...firstPart, action.payload, ...lastPart];
    },
    updateMessstellenWithNameChange: (state, action: PayloadAction<{ messstelle: messstelle, alteMessstelle: messstelle }>) => {
      const updateIndex = state.messstellenList.findIndex(
        (x) => x.name == action.payload.alteMessstelle.name
      );
      const firstPart = state.messstellenList.slice(0, updateIndex);
      let lastPart = Array<messstelle>();
      if (updateIndex + 1 < state.messstellenList.length)
        lastPart = state.messstellenList.slice(updateIndex + 1);
      state.messstellenList = [...firstPart, action.payload.messstelle, ...lastPart];
    },
    removeFromMessstellen: (state, action: PayloadAction<messstelle>) => {
      const updateIndex = state.messstellenList.findIndex(
        (x) => x.name == action.payload.name
      );
      const firstPart = state.messstellenList.slice(0, updateIndex);
      let lastPart = Array<messstelle>();
      if (updateIndex + 1 < state.messstellenList.length)
        lastPart = state.messstellenList.slice(updateIndex + 1);
      state.messstellenList = [...firstPart, ...lastPart];
    },
    addToMessstellen: (state, action: PayloadAction<messstelle>) => {
      const messstellen = state.messstellenList;
      messstellen.push(action.payload);
      state.messstellenList = [...messstellen].sort((a, b) => a.name.localeCompare(b.name));
    },
    setMessstellen: (state, action: PayloadAction<Array<messstelle>>) => {
      state.messstellenList = action.payload;
    },
    setAktuelleMessstelle: (state, action: PayloadAction<messstelle>) => {
      state.aktuelleMessstelle = action.payload;
      state.alteMessstelle = action.payload;
    },
    setAlteMessstelle: (state, action: PayloadAction<messstelle>) => {
      state.alteMessstelle = action.payload;
    },
    resetAktuelleMessstelle: (state) => {
      state.aktuelleMessstelle = state.alteMessstelle;
    },
    setUWs: (state, action: PayloadAction<Array<umspannwerk>>) => {
      state.uwList = action.payload;
    },
    setUW: (state, action: PayloadAction<string>) => {
      state.aktuelleMessstelle.umspannwerk = action.payload;
      if (state.aktuelleMessstelle.breitengrad === 0 && state.aktuelleMessstelle.laengengrad === 0) {
        const uw = state.uwList.find((uw) => uw.name === action.payload);
        state.aktuelleMessstelle.breitengrad = uw ? uw.breitengrad : 0;
        state.aktuelleMessstelle.laengengrad = uw ? uw.laengengrad : 0;
      } else {
        state.uwKoordinatenUebernehmenDialog = true;
      }
      state.editDirty = messstellenNotEqual(
        state.aktuelleMessstelle,
        state.alteMessstelle
      );
    },
    setSchaltfelder: (state, action: PayloadAction<Array<schaltfeld>>) => {
      state.schaltfelder = action.payload;
    },
    setSchaltfeld: (state, action: PayloadAction<string>) => {
      state.aktuelleMessstelle.schaltfeld = action.payload;
      state.editDirty = messstellenNotEqual(
        state.aktuelleMessstelle,
        state.alteMessstelle
      );
    },
    setSpannungsebene: (state, action: PayloadAction<string>) => {
      state.aktuelleMessstelle.spannungsebene = action.payload;
      state.editDirty = messstellenNotEqual(
        state.aktuelleMessstelle,
        state.alteMessstelle
      );
    },
    setSpannungsebenen: (state, action: PayloadAction<Array<string>>) => {
      state.spannungsebenen = action.payload;
    },
    setStromwandler: (state, action: PayloadAction<string>) => {
      state.aktuelleMessstelle.stromwandler = action.payload;
      state.editDirty = messstellenNotEqual(
        state.aktuelleMessstelle,
        state.alteMessstelle
      );
    },
    setStromwandlerListe: (state, action: PayloadAction<Array<stromwandler>>) => {
      state.stromwandler = action.payload;
      state.editDirty = messstellenNotEqual(
        state.aktuelleMessstelle,
        state.alteMessstelle
      );
    },
    setSpannungswandler: (state, action: PayloadAction<string>) => {
      state.aktuelleMessstelle.spannungswandler = action.payload;
      state.editDirty = messstellenNotEqual(
        state.aktuelleMessstelle,
        state.alteMessstelle
      );
    },
    setSpannungswandlerListe: (state, action: PayloadAction<Array<spannungswandler>>) => {
      state.spannungswandler = action.payload;
      state.editDirty = messstellenNotEqual(
        state.aktuelleMessstelle,
        state.alteMessstelle
      );
    },
    setName: (state, action: PayloadAction<string>) => {
      state.aktuelleMessstelle.name = action.payload;
      if (
        state.messstellenList.findIndex((x) => x.name == action.payload) !=
        elementNotFoud
      ) {
        state.dublicateName = true;
      } else {
        state.dublicateName = false;
      }
      state.editDirty = messstellenNotEqual(
        state.aktuelleMessstelle,
        state.alteMessstelle
      );
    },
    setBreitengrad: (state, action: PayloadAction<number>) => {
      state.aktuelleMessstelle.breitengrad = action.payload;
      state.editDirty = messstellenNotEqual(
        state.aktuelleMessstelle,
        state.alteMessstelle
      );
    },
    setLaengengrad: (state, action: PayloadAction<number>) => {
      state.aktuelleMessstelle.laengengrad = action.payload;
      state.editDirty = messstellenNotEqual(
        state.aktuelleMessstelle,
        state.alteMessstelle
      );
    },
    setKommentar: (state, action: PayloadAction<string>) => {
      state.aktuelleMessstelle.kommentar = action.payload;
      state.editDirty = messstellenNotEqual(
        state.aktuelleMessstelle,
        state.alteMessstelle
      );
    },
    setEditEntry: (state) => {
      state.editEntry = true;
    },
    resetEditEntry: (state) => {
      state.editEntry = false;
    },
    setNewEntry: (state) => {
      state.newEntry = true;
    },
    resetNewEntry: (state) => {
      state.newEntry = false;
    },
    resetEditDirty: (state) => {
      state.editDirty = false;
    },
    setViewMode: (state) => {
      state.viewMode = true;
    },
    resetViewMode: (state) => {
      state.viewMode = false;
    },
    setError: (state, action: PayloadAction<string>) => {
      if (!state.error.includes(action.payload)) {
        state.error.push(action.payload);
      }
    },
    resetError: (state, action: PayloadAction<string>) => {
      if (state.error.includes(action.payload)) {
        state.error.splice(state.error.indexOf(action.payload), 1)
      }
    },
    setUWKoordinatenUebernehmenDialog: (state, action: PayloadAction<boolean>) => {
      state.uwKoordinatenUebernehmenDialog = action.payload;
    },
    setKoordinatenFromUW: (state) => {
      const uw = state.uwList.find((uw) => uw.name === state.aktuelleMessstelle.umspannwerk);
      state.aktuelleMessstelle.breitengrad = uw ? uw.breitengrad : 0;
      state.aktuelleMessstelle.laengengrad = uw ? uw.laengengrad : 0;
      state.uwKoordinatenUebernehmenDialog = false;
    },
    setListLoading: (state, action: PayloadAction<boolean>) => {
      state.listLoading = action.payload;
    },
    setUwLoading: (state, action: PayloadAction<boolean>) => {
      state.uwLoading = action.payload;
    },
    setSpannungswandlerLoading: (state, action: PayloadAction<boolean>) => {
      state.spannungswandlerLoading = action.payload;
    },
    setStromwandlerLoading: (state, action: PayloadAction<boolean>) => {
      state.stromwandlerLoading = action.payload;
    },
    setWandlerLoading: (state, action: PayloadAction<boolean>) => {
      state.stromwandlerLoading = action.payload;
    },
    setSpannungsebeneLoading: (state, action: PayloadAction<boolean>) => {
      state.spannungsebeneLoading = action.payload;
    },
    setSchaltfeldLoading: (state, action: PayloadAction<boolean>) => {
      state.schaltfeldLoading = action.payload;
    },
    setSorting: (state, action: PayloadAction<GridSortModel>) => {
      state.sorting = action.payload;
    },
  },
});

//Selectors
//======================================================================
export const selectMessstellen = (state: RootState): messstelle[] =>
  state.messstelle.messstellenList;
export const selectAktuelleMessstelle = (state: RootState): messstelle =>
  state.messstelle.aktuelleMessstelle;
export const selectAlteMessstelle = (state: RootState): messstelle =>
  state.messstelle.alteMessstelle;
export const selectUWs = (state: RootState): umspannwerk[] => state.messstelle.uwList;
export const selectSchaltfelder = (state: RootState): schaltfeld[] =>
  state.messstelle.schaltfelder;
export const selectSpannungsebene = (state: RootState): string[] =>
  state.messstelle.spannungsebenen;
export const selectStromwandler = (state: RootState): stromwandler[] =>
  state.messstelle.stromwandler;
export const selectSpannungswandler = (state: RootState): spannungswandler[] =>
  state.messstelle.spannungswandler;
export const selectEditEntry = (state: RootState): boolean => state.messstelle.editEntry;
export const selectNewEntry = (state: RootState): boolean => state.messstelle.newEntry;
export const selectDublicatName = (state: RootState): boolean =>
  state.messstelle.dublicateName;
export const selectEditDirty = (state: RootState): boolean => state.messstelle.editDirty;
export const selectViewMode = (state: RootState): boolean => state.messstelle.viewMode;
export const selectError = (state: RootState): boolean => state.messstelle.error.length > 0;
export const selectUWKoordinatenUebernehmenDialog = (state: RootState): boolean => state.messstelle.uwKoordinatenUebernehmenDialog;
export const selectListLoading = (state: RootState): boolean => state.messstelle.listLoading;
export const selectUwLoading = (state: RootState): boolean => state.messstelle.uwLoading;
export const selectSpannungswandlerLoading = (state: RootState): boolean => state.messstelle.spannungswandlerLoading;
export const selectStromwandlerLoading = (state: RootState): boolean => state.messstelle.stromwandlerLoading;
export const selectSpannungsebeneLoading = (state: RootState): boolean => state.messstelle.spannungsebeneLoading
export const selectSchaltfeldLoading = (state: RootState): boolean => state.messstelle.schaltfeldLoading;
export const selectSorting = (state: RootState): GridSortModel => state.messstelle.sorting;

//Actions
//=====================================================================
export const getMessstellen = (idString: string | undefined) => (dispatch: AppDispatch): void => {
  dispatch(setListLoading(true));
  getWithAuth("MESSSTELLE", {})
    .then((response: Array<messstelle>) => {
      dispatch(setMessstellen(response.map((x: messstelle, index: number) => ({ ...x, id: index }))));
      if (idString) {
          const entry = response.find(x => x.name == idString);
          if (entry) dispatch(updateAktuelleMessstelle(entry));
      }
    })
    .catch((error) => {
      logError(error);
      errorHandler(error, dispatch);
    }).finally(() => dispatch(setListLoading(false)));
};

export const getUWs = () => (dispatch: AppDispatch): void => {
  dispatch(setUwLoading(true))
  getWithAuth("UMSPANNWERK", {})
    .then((response) => {
      dispatch(setUWs(response));
    })
    .catch((error) => {
      logError(error);
      errorHandler(error, dispatch);
    }).finally(() => dispatch(setUwLoading(false)));
};
export const getSchaltfelder = () => (dispatch: AppDispatch): void => {
  dispatch(setSchaltfeldLoading(true))
  getWithAuth("SCHALTFELD", {})
    .then((response) => {
      dispatch(setSchaltfelder(response));
    })
    .catch((error) => {
      logError(error);
      errorHandler(error, dispatch);
    }).finally(() => dispatch(setSchaltfeldLoading(false)));
};

export const getSapnnungsebene = () => (dispatch: AppDispatch): void => {
  dispatch(setSpannungsebeneLoading(true));
  getWithAuth("SPANNUNGSEBENE", {})
    .then((response: spannungsebene[]) => {
      dispatch(setSpannungsebenen(response.map((x) => x.ebene)));
    })
    .catch((error) => {
      logError(error);
      errorHandler(error, dispatch);
    }).finally(() => dispatch(setSpannungsebeneLoading(false)));
};

export const getStromwandler = () => (dispatch: AppDispatch): void => {
  dispatch(setStromwandlerLoading(true));
  getWithAuth("STROMWANDLER", {})
    .then((response: stromwandler[]) => {
      dispatch(setStromwandlerListe(response));
    })
    .catch((error) => {
      logError(error);
      errorHandler(error, dispatch);
    }).finally(() => dispatch(setStromwandlerLoading(false)));
};

export const getSpannungswandler = () => (dispatch: AppDispatch): void => {
  dispatch(setSpannungswandlerLoading(true));
  getWithAuth("SPANNUNGSWANDLER", {})
    .then((response: spannungswandler[]) => {
      dispatch(setSpannungswandlerListe(response));
    })
    .catch((error) => {
      logError(error);
      errorHandler(error, dispatch);
    }).finally(() => dispatch(setSpannungswandlerLoading(false)));
};

export const updateAktuelleMessstelle = (messstelle: messstelle): ThunkAction<void, RootState, unknown, AnyAction> => (
  dispatch: AppDispatch,
  getState: () => RootState
) => {
  if (!getState().messstelle.editEntry) {
    dispatch(setAktuelleMessstelle(messstelle));
    dispatch(clearMessstellenForLookup());
    dispatch(setViewMode());
  }
};

export const updateUmspannwerk = (uw: string) => (dispatch: AppDispatch): void => {
  dispatch(setUW(uw));
  dispatch(setSchaltfeld(""));
};

export const deleteMessstelle = (messstelle: messstelle): ThunkAction<void, RootState, unknown, AnyAction> => (
  dispatch: AppDispatch
) => {
  dispatch(setListLoading(true));
  deleteWithAuth("MESSSTELLEID", {}, messstelle.name)
    .then(() => dispatch(setAktuelleMessstelle(messstelleDummy)))
    .then(() => dispatch(removeFromMessstellen(messstelle)))
    .then(() => dispatch(resetEditEntry()))
    .then(() => dispatch(resetEditDirty()))
    .then(() => dispatch(resetViewMode()))
    .then(() => dispatch(
      openSnackbar({
        message: `${messstelle.name} wurde erfolgreich gelöscht.`,
        severity: "success",
        duration: 6000,
      })
    ))
    .catch((error) => {
      logError(error);
      errorHandler(error, dispatch);
    }).finally(() => dispatch(setListLoading(false)));


};

export const recreateMessstelle = (messstelle: messstelle, oldMessstelle: messstelle): ThunkAction<void, RootState, unknown, AnyAction> => (
  dispatch: AppDispatch
) => {
  dispatch(setListLoading(true));
  deleteWithAuth("MESSSTELLEID", {}, oldMessstelle.name)
    .then(() =>
      putWithAuth("MESSSTELLEID", {}, messstelle, messstelle.name)
        .then(() =>
          dispatch(
            openSnackbar({
              message: `${messstelle.name} wurde erfolgreich geändert.`,
              severity: "success",
              duration: 6000,
            })
          ),
        ).then(() => dispatch(setAlteMessstelle(messstelle)))
        .then(() => dispatch(updateMessstellenWithNameChange({ messstelle: messstelle, alteMessstelle: oldMessstelle })))
        .then(() => dispatch(resetEditEntry()))
        .then(() => dispatch(resetEditDirty()))
        .then(() => dispatch(clearMessstellenForLookup()))
        .catch((error) => {
          logError(error);
          errorHandler(error, dispatch);
        })
    )
    .catch((error) => {
      logError(error);
      errorHandler(error, dispatch);
    }).finally(() => dispatch(setListLoading(false)));


};

export const updateMessstelle = (messstelle: messstelle): ThunkAction<void, RootState, unknown, AnyAction> => (
  dispatch: AppDispatch
) => {
  dispatch(setListLoading(true));
  putWithAuth("MESSSTELLEID", {}, messstelle, messstelle.name)
    .then(() => dispatch(clearMessstellenForLookup()))
    .then(() =>
      dispatch(
        openSnackbar({
          message: `${messstelle.name} wurde erfolgreich geändert.`,
          severity: "success",
          duration: 6000,
        })
      )
    )
    .catch((error) => {
      logError(error);
      errorHandler(error, dispatch);
    }).finally(() => dispatch(setListLoading(false)));
  dispatch(setAlteMessstelle(messstelle));
  dispatch(updateMessstellen(messstelle));
  dispatch(resetEditEntry());
  dispatch(resetEditDirty());
};

export const discardChanges = () => (dispatch: AppDispatch): void => {
  dispatch(resetAktuelleMessstelle());
  dispatch(resetEditEntry());
  dispatch(resetEditDirty());
};

export const discardNew = () => (dispatch: AppDispatch): void => {
  dispatch(resetAktuelleMessstelle());
  dispatch(resetEditEntry());
  dispatch(resetNewEntry());
  dispatch(resetEditDirty());
  dispatch(resetViewMode());
};

export const startNewEntry = () => (dispatch: AppDispatch): void => {
  dispatch(setNewEntry());
  dispatch(setAktuelleMessstelle(messstelleDummy));
  dispatch(setEditEntry());
  dispatch(setViewMode());
};

export const createMessstelle = (messstelle: messstelle): ThunkAction<void, RootState, unknown, AnyAction> => (
  dispatch: AppDispatch
) => {
  dispatch(setListLoading(true));
  putWithAuth("MESSSTELLEID", {}, messstelle, messstelle.name)
    .then(() => {
      dispatch(
        openSnackbar({
          message: `${messstelle.name} wurde erfolgreich angelegt.`,
          severity: "success",
          duration: 6000,
        })
      );
      dispatch(addToMessstellen(messstelle));
      dispatch(setAktuelleMessstelle(messstelleDummy));
      dispatch(resetEditEntry());
      dispatch(resetNewEntry());
      dispatch(resetEditDirty());
      dispatch(resetViewMode());
      dispatch(clearMessstellenForLookup());
    }
    )
    .catch((error) => {
      logError(error);
      errorHandler(error, dispatch);
    }).finally(() => dispatch(setListLoading(false)));
};

export const {
  setMessstellen,
  setAktuelleMessstelle,
  setUWs,
  setSchaltfelder,
  setUW,
  setSchaltfeld,
  setSpannungsebene,
  setSpannungsebenen,
  setStromwandler,
  setStromwandlerListe,
  setSpannungswandlerListe,
  setSpannungswandler,
  resetAktuelleMessstelle,
  setBreitengrad,
  setKommentar,
  setLaengengrad,
  setAlteMessstelle,
  updateMessstellen,
  setEditEntry,
  resetEditEntry,
  setNewEntry,
  resetNewEntry,
  setName,
  addToMessstellen,
  resetEditDirty,
  setViewMode,
  resetViewMode,
  updateMessstellenWithNameChange,
  removeFromMessstellen,
  setError,
  resetError,
  setUWKoordinatenUebernehmenDialog,
  setKoordinatenFromUW,
  setListLoading,
  setSpannungswandlerLoading,
  setStromwandlerLoading,
  setWandlerLoading,
  setSpannungsebeneLoading,
  setSchaltfeldLoading,
  setUwLoading,
  setSorting
} = messstellenSlice.actions;
export default messstellenSlice.reducer;
